import {memo, useState} from 'react'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {useTheme} from 'styled-components'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {
    AlertCircleIcon,
    CheckCircleIcon,
    ChevronRightIcon,
    ClockRewindIcon,
    DotsHorizontalIcon,
    Download01Icon,
    FolderIcon,
    QueueListIcon,
    Repeat02Icon,
    SlashCircle01Icon,
    Trash01Icon
} from '@/components/ui/icon/Icon'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {
    ExportJob,
    canArchiveExport,
    canCancelExport,
    canDeleteExport,
    canDownloadExport,
    canRetryExport
} from '@/features/exports/types'
import {getEntityLabelKey} from '@/features/exports/utils/entities'
import {statusLabelKey} from '@/features/exports/utils/status'
import {summarizeAppliedFilters} from '@/features/exports/utils/appliedFilters'
import {summarizeColumns} from '@/features/exports/utils/columns'
import {formatFileSize, formatRequestedAt} from '@/features/exports/utils/format'
import {
    StyledDownloadName,
    StyledExportMetaLine,
    StyledExportRow,
    StyledKebabIcon,
    StyledMetaAccordionContent,
    StyledMetaAccordionTrigger,
    StyledStatusIcon
} from './style'

/**
 * Leading status indicator: a simple icon instead of a textual badge.
 * `pending` reads as "queued", `processing` shows a live spinner; terminal states use a
 * meaningful glyph. The textual status is still exposed via the wrapper's `title`/`aria-label`.
 */
const StatusIcon = ({status}: {status: string}) => {
    const {palette} = useTheme()
    switch (status) {
        case 'processing':
            return <Spinner size={16} fill={palette.orange[500]} />
        case 'ready':
            return <CheckCircleIcon size={18} fill={palette.success[500]} />
        case 'failed':
            return <AlertCircleIcon size={18} fill={palette.danger[500]} />
        case 'cancelled':
            return <SlashCircle01Icon size={18} fill={palette.neutral[400]} />
        case 'archived':
            return <FolderIcon size={18} fill={palette.neutral[400]} />
        case 'expired':
            return <ClockRewindIcon size={18} fill={palette.neutral[400]} />
        case 'pending':
        default:
            return <QueueListIcon size={18} fill={palette.neutral[500]} />
    }
}

interface ExportRowProps {
    job: ExportJob
    showEntity?: boolean
    isDownloading: boolean
    isRetrying: boolean
    isCancelling: boolean
    isArchiving: boolean
    isDeleting: boolean
    onDownload: (job: ExportJob) => void
    onRetry: (id: string) => void
    onCancel: (id: string) => void
    onArchive: (id: string) => void
    onDelete: (id: string) => void
}

const ExportRowComponent = ({
    job,
    showEntity = false,
    isDownloading,
    isRetrying,
    isCancelling,
    isArchiving,
    isDeleting,
    onDownload,
    onRetry,
    onCancel,
    onArchive,
    onDelete
}: ExportRowProps) => {
    const {t} = useTranslation()
    const [isMetaOpen, setIsMetaOpen] = useState(false)

    const labelKey = statusLabelKey(job.status)
    const filtersSummary = summarizeAppliedFilters(job.entity, job.appliedFilters, t)
    const columnsSummary = summarizeColumns(job.entity, job.appliedColumns, t)
    const columnsCount = job.appliedColumns?.length ?? 0
    const filtersCount = filtersSummary.length
    const columnsTriggerLabel =
        columnsCount > 0 ? t('exports:columns_count', {count: columnsCount}) : t('exports:default_columns')
    const filtersTriggerLabel =
        filtersCount > 0 ? t('exports:filters_count', {count: filtersCount}) : t('exports:no_filters')
    const fileSize = formatFileSize(job.fileSizeBytes)
    const isBusy = isDownloading || isRetrying || isCancelling || isArchiving || isDeleting

    const canDownload = canDownloadExport(job)
    const canRetry = canRetryExport(job)
    const canCancel = canCancelExport(job)
    const canArchive = canArchiveExport(job)
    const canDelete = canDeleteExport(job)
    const hasActions = canDownload || canRetry || canCancel || canArchive || canDelete

    const statusTitle = labelKey ? t(labelKey) : job.status

    return (
        <StyledExportRow align="flex-start" gap={3} fullWidth>
            <Tooltip
                content={statusTitle}
                isOpenOnClickEnabled={false}
                contentProps={{side: 'top'}}
                triggerProps={{asChild: true}}
            >
                <StyledStatusIcon role="img" aria-label={statusTitle}>
                    <StatusIcon status={job.status} />
                </StyledStatusIcon>
            </Tooltip>

            <Flexbox direction="column" gap={2} fullWidth>
                <Flexbox justify="space-between" align="flex-start" gap={2} fullWidth>
                    <Flexbox align="center" gap={2} style={{flexWrap: 'wrap'}}>
                        {showEntity && (
                            <Text size="textSm" palette="neutral" shade="700" weight="500">
                                {t(getEntityLabelKey(job.entity))}
                            </Text>
                        )}
                    </Flexbox>

                    <Flexbox align="center" gap={2}>
                        <Text size="textSm" palette="neutral" shade="500">
                            {formatRequestedAt(job.requestedAt)}
                        </Text>
                        {hasActions && (
                            <MenuProvider>
                                <MenuButton
                                    render={
                                        <Button size="xs" shape="square" variant="tertiary" disabled={isBusy}>
                                            <StyledKebabIcon>
                                                <DotsHorizontalIcon />
                                            </StyledKebabIcon>
                                        </Button>
                                    }
                                />
                                <Menu width="200px">
                                    <MenuGroup>
                                        {canDownload && (
                                            <MenuItem disabled={isBusy} onClick={() => onDownload(job)}>
                                                <Download01Icon />
                                                {t('exports:download')}
                                            </MenuItem>
                                        )}
                                        {canRetry && (
                                            <MenuItem disabled={isBusy} onClick={() => onRetry(job.id)}>
                                                <Repeat02Icon />
                                                {t('exports:retry')}
                                            </MenuItem>
                                        )}
                                        {canArchive && (
                                            <MenuItem disabled={isBusy} onClick={() => onArchive(job.id)}>
                                                <FolderIcon />
                                                {t('exports:archive')}
                                            </MenuItem>
                                        )}
                                    </MenuGroup>
                                    {(canCancel || canDelete) && (
                                        <MenuGroup>
                                            {canCancel && (
                                                <MenuItem
                                                    variant="danger"
                                                    disabled={isBusy}
                                                    onClick={() => onCancel(job.id)}
                                                >
                                                    <SlashCircle01Icon />
                                                    {t('exports:cancel')}
                                                </MenuItem>
                                            )}
                                            {canDelete && (
                                                <MenuItem
                                                    variant="danger"
                                                    disabled={isBusy}
                                                    onClick={() => onDelete(job.id)}
                                                >
                                                    <Trash01Icon />
                                                    {t('exports:delete')}
                                                </MenuItem>
                                            )}
                                        </MenuGroup>
                                    )}
                                </Menu>
                            </MenuProvider>
                        )}
                    </Flexbox>
                </Flexbox>

                <StyledExportMetaLine align="center">
                    {job.fileName &&
                        (canDownload ? (
                            <StyledDownloadName type="button" disabled={isDownloading} onClick={() => onDownload(job)}>
                                {job.fileName}
                            </StyledDownloadName>
                        ) : (
                            <Text size="textSm" palette="neutral" shade="900" weight="500">
                                {job.fileName}
                            </Text>
                        ))}
                    <Text size="textXs" palette="neutral" shade="500">
                        {job.format.toUpperCase()}
                    </Text>
                    {fileSize && (
                        <Text size="textXs" palette="neutral" shade="500">
                            {fileSize}
                        </Text>
                    )}
                    {typeof job.selectionSummary === 'number' && (
                        <Text size="textXs" palette="neutral" shade="500">
                            {t('exports:records', {count: job.selectionSummary})}
                        </Text>
                    )}
                </StyledExportMetaLine>

                <Flexbox direction="column" gap={1} fullWidth>
                    <StyledMetaAccordionTrigger
                        type="button"
                        aria-expanded={isMetaOpen}
                        onClick={() => setIsMetaOpen(open => !open)}
                    >
                        <ChevronRightIcon />
                        {columnsTriggerLabel} - {filtersTriggerLabel}
                    </StyledMetaAccordionTrigger>
                    {isMetaOpen && (
                        <StyledMetaAccordionContent fullWidth>
                            <Text size="textXs" palette="neutral" shade="600">
                                {<b style={{fontWeight: 'bold'}}>{t('exports:filters_label')}</b>}:{' '}
                                {filtersSummary.length > 0
                                    ? filtersSummary
                                          .map(filter =>
                                              [filter.field, filter.operator, filter.value].filter(Boolean).join(' ')
                                          )
                                          .join(' · ')
                                    : t('exports:no_filters')}
                            </Text>
                            <Text size="textXs" palette="neutral" shade="600">
                                {<b style={{fontWeight: 'bold'}}>{t('exports:columns_label')}</b>}:{' '}
                                {columnsSummary || t('exports:default_columns')}
                            </Text>
                        </StyledMetaAccordionContent>
                    )}
                </Flexbox>

                {job.status === 'failed' && job.failureReason?.message && (
                    <Text size="textXs" palette="danger" shade="600">
                        {job.failureReason.message}
                    </Text>
                )}
            </Flexbox>
        </StyledExportRow>
    )
}

export const ExportRow = memo(ExportRowComponent)
